import {
  VARIANCE_ACTIONS,
  varianceActionTargetFile,
} from './varianceActions.js';

const normalize = (value) => String(value ?? '').trim().toLowerCase();
const normalizeCode = (value) => String(value ?? '').trim().toUpperCase();

const ACTIONS_THAT_RESOLVE_FINDINGS = new Set([
  VARIANCE_ACTIONS.REMOVE_FROM_GM,
  VARIANCE_ACTIONS.REMOVE_FROM_CPQ,
  VARIANCE_ACTIONS.REPLACE_PRODUCT,
  VARIANCE_ACTIONS.CUSTOM,
]);

const rawRowForFinding = (finding = {}) => finding.rawRow || finding.raw_row || {};

const actionFileKind = (action = {}) => {
  const target = action.targetSource || action.targetFile || varianceActionTargetFile(action.actionType);
  if (target === 'gm' || target === 'cpq') return target;
  if (action.source === 'gm' || action.source === 'cpq') return action.source;
  return '';
};

const actionRowKeys = (action = {}) => [
  action.sourceRowKey,
  action.targetRowKey,
  action.sourceEvidence?.sourceRowKey,
  action.sourceEvidence?.rowKey,
  action.sourceEvidence?.rowNumber,
  action.targetEvidence?.sourceRowKey,
  action.targetEvidence?.rowKey,
  action.targetEvidence?.rowNumber,
].filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map(normalize);

const findingRowKeys = (finding = {}) => [
  finding.sourceRowKey,
  finding.rowKey,
  finding.rowNumber,
  rawRowForFinding(finding).__fileHealthRowNumbers,
  rawRowForFinding(finding).rowNumbers,
  finding.rowNumbers,
].flat().filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map(normalize);

const actionProductCodes = (action = {}) => [
  action.productCode,
  action.customProductCode,
  action.replacementProductCode,
  action.sourceProductCode,
  action.sourceEvidence?.productCode,
  action.sourceEvidence?.normalizedProductCode,
  action.sourceEvidence?.oemProductCode,
  action.targetEvidence?.productCode,
  action.targetEvidence?.normalizedProductCode,
  action.targetEvidence?.oemProductCode,
].filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map(normalizeCode);

const findingProductCodes = (finding = {}) => {
  const raw = rawRowForFinding(finding);
  return [
    finding.productCode,
    finding.product_code,
    finding.normalizedProductCode,
    finding.normalized_product_code,
    raw['Product Code'],
    raw['OEM Product Code'],
    raw['Normalized Product Code'],
    raw['Formula Product Code'],
    raw['Order Product: OEM Product Code (Formula)'],
  ].filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
    .map((value) => normalizeCode(String(value).replace(/^=/, '')));
};

const hasIntersection = (left = [], right = []) => left.some((value) => right.includes(value));

export const fileHealthFindingResolvedByAction = (finding = {}, action = {}) => {
  if (!ACTIONS_THAT_RESOLVE_FINDINGS.has(action.actionType)) return false;
  const fileKind = normalize(finding.fileKind);
  if (!fileKind || actionFileKind(action) !== fileKind) return false;

  const rowKeys = findingRowKeys(finding);
  const actionKeys = actionRowKeys(action);
  if (rowKeys.length && actionKeys.length && hasIntersection(rowKeys, actionKeys)) return true;

  if (finding.code === 'CPQ_DUPLICATE_PRODUCT') {
    const findingCodes = findingProductCodes(finding);
    const actionCodes = actionProductCodes(action);
    return Boolean(findingCodes.length && actionCodes.length && hasIntersection(findingCodes, actionCodes));
  }

  return false;
};

export const resolvedFileHealthFindings = (findings = [], actions = []) => {
  const actionable = actions.filter((action) => action?.saveStatus !== 'discarded');
  return findings.filter((finding) => (
    !actionable.some((action) => fileHealthFindingResolvedByAction(finding, action))
  ));
};
